<template>
  <v-container style="padding: 0">
    <v-form ref="labelsForm" v-model="isValidComputed" class="pl-6 pb-2">
      <style-dialog-style-rows
        :has-copy-button="symbolizationType !== 'single'"
        :items="labelsStyleInputs"
        v-model="computedLabelsStyle"
        style-type="label"
      >
        <template #attributes="scope">
          <data-input
            v-bind="scope"
            v-model="computedLabelsStyle['attributes']"
            :disabled="Boolean(computedLabelsStyle.attributes_advanced?.text)"
          >
            <template #selection="{ item, index }">
              <v-tooltip v-if="index === 0" bottom color="font">
                <template #activator="{ on }">
                  <v-chip v-on="on" x-small>
                    <div class="text-truncate">{{ item.text }}</div>
                  </v-chip>
                </template>
                {{ item.text }}
              </v-tooltip>
              <div v-if="index === 1" class="grey--text caption" style="white-space: nowrap !important;">
                {{ otherAttributesText }}
              </div>
            </template>
          </data-input>
        </template>
        <template #attributes.input.append>
          <v-col cols="1" class="text-left pl-2">
            <v-icon color="primary" @click="openLabelsAttributes">mdi-cog</v-icon>
          </v-col>
        </template>
        <template #zoom="input">
          <style-dialog-zoom-input
            :inputBind="input"
            :minzoom.sync="computedLabelsStyle['minzoom']"
            :maxzoom.sync="computedLabelsStyle['maxzoom']"
          />
        </template>
      </style-dialog-style-rows>
    </v-form>
    <style-dialog-labels-preview :labels-style="computedLabelsStyle" :is-style-valid="isValidComputed" />
    <main-large-dialog
      :is-visible.sync="labelsDialogVisible"
      agree-text="dialog.agreeSave"
      :title="$i18n.t('dialog.style.labelsSettings')"
      @agree="saveLabels"
      dialog-width="60%"
      min-body-height="450px"
    >
      <template #body>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="12">
              <v-alert
                icon="mdi-information-outline"
                color="primary"
                style="font-size: 12px"
                class="text-left"
                dense
                text
              >
                {{ $i18n.t('dialog.style.labelsSettingsAlert') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-icon
              v-for="(icon, idx) of labelsSettingsIcons"
              @click="currentAttributesAdvanced.justify = icon.name"
              :key="idx"
              size="18"
              class="mr-1"
              :class="{ 'active-labels-style-justify': currentAttributesAdvanced?.justify === icon.name }"
            >
              {{ icon.icon }}
            </v-icon>
          </v-row>
          <v-row no-gutters>
            <v-col cols="8" class="pr-3">
              <data-input
                :class="`style-dialog-advanced-labels-text-${currentAttributesAdvanced.justify}`"
                ref="labels-input"
                v-bind="labelsSettingsInput"
                v-model="currentAttributesAdvanced.text"
              />
            </v-col>
            <v-col cols="4">
              <v-treeview
                :items="layerAttributesSchema"
                itemChildren="elements"
                class="text-left treeview"
                hoverable
                dense
              >
                <template #label="{ item }">
                  <v-hover #default="{ hover }">
                    <div class="d-flex justify-space-between align-center">
                      <span class="treeview__label">
                        {{ item.label }}
                      </span>
                      <v-tooltip v-if="!item.elements && hover" color="font" bottom>
                        <template #activator="{ on }">
                          <v-btn
                            v-on="on"
                            color="success"
                            height="18"
                            width="18"
                            depressed
                            fab
                            @click="addToAttributesAdvances(item.attributeValue)"
                          >
                            <v-icon small>
                              mdi-plus
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $i18n.t('dialog.style.addToLabel') }}</span>
                      </v-tooltip>
                    </div>
                  </v-hover>
                </template>
              </v-treeview>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </main-large-dialog>
  </v-container>
</template>
<script>
import { get } from 'vuex-pathify';
import style from '@/mixins/style';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import StyleDialogStyleRows from '@/components/StyleDialogStyleRows';
import StyleDialogLabelsPreview from '@/components/StyleDialogLabelsPreview';
import StyleDialogZoomInput from '@/components/StyleDialogZoomInput';

export default {
  name: 'StyleDialogLabelsTab',
  mixins: [style],
  components: {
    DataInput,
    MainLargeDialog,
    StyleDialogStyleRows,
    StyleDialogLabelsPreview,
    StyleDialogZoomInput,
  },
  props: {
    attributes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    isValid: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    labelsStyle: {
      type: Object,
      default: () => {
        return {};
      },
    },
    symbolizationType: {
      type: String,
      default: 'point',
    },
    geometryType: {
      type: String,
      required: true,
    },
    layerId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    layers: get('layers/layers'),
    layerAttributesSchema() {
      return (this.layers[this.layerId]?.form_schema.elements || []).map(group => {
        return {
          ...group,
          elements: group.elements.map(attribute => {
            return {
              ...attribute,
              attributeValue:
                this.attributes.find(attr => attr.value === attribute.attribute)?.valueLabel || attribute.attribute,
            };
          }),
        };
      });
    },
    computedLabelsStyle: {
      get() {
        return this.labelsStyle;
      },
      set(nV) {
        this.$emit('update:labelsStyle', nV);
      },
    },
    labelsStyleInputs() {
      if (['linestring', 'multilinestring'].includes(this.geometryType)) {
        return this.labelsItems.filter(item => item.name !== 'offset-x');
      }
      return this.labelsItems;
    },
    isValidComputed: {
      get() {
        return this.isValid;
      },
      set(nV) {
        this.$emit('update:isValid', nV);
      },
    },
    otherAttributesText() {
      const otherAttributesLength = this.computedLabelsStyle.attributes.length - 1;
      return `(+${this.$i18n.tc('admin.other', otherAttributesLength)})`;
    },
    labelsSettingsIcons() {
      return [
        { name: 'left', icon: 'mdi-format-align-left' },
        { name: 'center', icon: 'mdi-format-align-center' },
        { name: 'right', icon: 'mdi-format-align-right' },
      ];
    },
    labelsSettingsInput() {
      return {
        dataType: { name: 'textarea' },
        clearable: true,
        hideDetails: true,
        height: '285px',
        noResize: true,
      };
    },
  },
  data: () => ({
    currentAttributesAdvanced: { justify: 'center', text: '' },
    labelsDialogVisible: false,
  }),
  methods: {
    openLabelsAttributes() {
      this.currentAttributesAdvanced = JSON.parse(
        JSON.stringify(this.computedLabelsStyle.attributes_advanced || { justify: 'center', text: '' })
      );
      this.labelsDialogVisible = true;
    },
    addToAttributesAdvances(attribute) {
      const cursorPosition = this.$refs['labels-input']?.$refs['data-input']?.$refs.input?.$refs.input?.selectionStart;
      const text = `{${attribute}}`;
      this.currentAttributesAdvanced.text =
        this.currentAttributesAdvanced.text.slice(0, cursorPosition) +
        text +
        this.currentAttributesAdvanced.text.slice(cursorPosition);
      this.$refs['labels-input']?.$refs['data-input']?.$refs.input?.$refs.input?.focus();
      this.$nextTick(() => {
        const textEnd = this.currentAttributesAdvanced.text.slice(0, cursorPosition).length + text.length;
        this.$refs['labels-input']?.$refs['data-input']?.$refs.input?.$refs.input?.setSelectionRange(textEnd, textEnd);
      });
    },
    saveLabels() {
      this.computedLabelsStyle = { ...this.computedLabelsStyle, attributes_advanced: this.currentAttributesAdvanced };
      this.labelsDialogVisible = false;
    },
  },
  watch: {
    'computedLabelsStyle.attributes': {
      handler(nV, oV) {
        const items = this.labelsItems.find(labelItem => labelItem.name === 'attributes')?.items || [];
        const fixedValue = nV.filter(value => items.find(item => item.valueLabel === value));
        if (JSON.stringify(nV) !== JSON.stringify(fixedValue))
          this.$set(this.computedLabelsStyle, 'attributes', fixedValue);
        if (!oV || (oV.length === 0 && nV.length > 0)) {
          const params = {
            'font-size': 12,
            'font-weight': 'normal',
            minzoom: 12,
            maxzoom: null,
            'offset-x': 0,
            'offset-y': 0,
            'stroke-width': 3,
          };
          for (const param of Object.keys(params)) {
            if (!this.computedLabelsStyle[param]) {
              this.$set(this.computedLabelsStyle, param, params[param]);
            }
          }
        }
      },
      immediate: true,
      deep: true,
    },
    'currentAttributesAdvanced.text': {
      handler(nV) {
        if (nV === null) this.currentAttributesAdvanced.text = '';
      },
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-input--checkbox {
    margin-top: 0 !important;
    .v-label {
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 13px;
      line-height: 15px;
      color: #202124;
    }
  }
  .treeview {
    background-color: rgba(228, 242, 254, 0.6);
    border: 1px solid #d5ebfe;
    border-radius: 4px;
    overflow-y: auto;
    max-height: 285px;
  }
}
.active-labels-style-justify {
  color: map-get($colors, 'primary');
  background-color: map-get($colors, 'primarybackground');
}
$justifies: (left, center, right);
@each $justify in $justifies {
  .style-dialog-advanced-labels-text-#{$justify} {
    ::v-deep {
      textarea {
        text-align: $justify;
      }
    }
  }
}
</style>
